import { createContext, useContext, type ReactNode } from 'react';
import {
  buildResolvedFoundrySettings,
  localFoundrySettingsCapabilities,
  type FoundrySettingsHostCapabilities,
  type FoundrySettingId,
  type ResolvedFoundrySetting,
  type ResolvedFoundrySettingsSnapshot,
} from '@volley/crucible/runtime/resolved-settings.js';
import type { AppFrame } from '../AppFrameContext';

const TEST_LOCAL_DEFAULT = buildResolvedFoundrySettings({
  placement: 'local',
  capabilities: localFoundrySettingsCapabilities(),
});

const FAIL_CLOSED_CAPABILITIES: FoundrySettingsHostCapabilities = {
  durableConfig: false,
  credentials: false,
  localWorkspace: false,
  livePreviewPreference: false,
  designPreference: false,
  componentTemplateStatus: false,
  localPreviewDevices: false,
  furnaceStatus: false,
  analyticsPreference: false,
  autoUpdater: false,
  diagnostics: false,
};

// Keep both host identities available so loading never implies the wrong runtime policy.
export const FAIL_CLOSED_SETTINGS = buildResolvedFoundrySettings({
  placement: 'local',
  capabilities: FAIL_CLOSED_CAPABILITIES,
});
const CLOUD_FAIL_CLOSED_SETTINGS = buildResolvedFoundrySettings({
  placement: 'cloud',
  capabilities: FAIL_CLOSED_CAPABILITIES,
});

const ResolvedSettingsContext = createContext<ResolvedFoundrySettingsSnapshot | null>(null);

/**
 * Selects a fail-closed Settings snapshot matching the active application frame.
 *
 * Args:
 *   frame: Surface frame whose host identity must be preserved while settings load.
 *
 * Returns:
 *   An unavailable Settings snapshot with host-appropriate explanations.
 *
 * Raises:
 *   None.
 */
export function failClosedSettingsForFrame(frame: AppFrame): ResolvedFoundrySettingsSnapshot {
  // Embedded is the Cloud browser surface; fullscreen is the local Desktop surface.
  return frame === 'embedded' ? CLOUD_FAIL_CLOSED_SETTINGS : FAIL_CLOSED_SETTINGS;
}

export function ResolvedSettingsProvider({
  snapshot,
  children,
}: {
  snapshot: ResolvedFoundrySettingsSnapshot;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <ResolvedSettingsContext.Provider value={snapshot}>{children}</ResolvedSettingsContext.Provider>
  );
}

export function useResolvedSetting(id: FoundrySettingId): ResolvedFoundrySetting {
  // Exported panels are rendered directly by focused unit tests. The local
  // default preserves that test seam; production always mounts the provider
  // in <Settings> and therefore never infers availability in the renderer.
  const snapshot = useContext(ResolvedSettingsContext) ?? TEST_LOCAL_DEFAULT;
  const setting = snapshot.settings.find((candidate) => candidate.id === id);
  if (setting === undefined) {
    throw new Error(`Resolved Settings snapshot is missing required setting '${id}'.`);
  }
  return setting;
}

export function SettingUnavailableReason({
  setting,
}: {
  setting: ResolvedFoundrySetting;
}): React.JSX.Element | null {
  if (setting.availability !== 'unavailable' || setting.unavailableReason === null) return null;
  return (
    <p className="mt-1 text-xs text-amber-300" role="note" data-setting-unavailable={setting.id}>
      {setting.unavailableReason}
    </p>
  );
}
